// AdminProducerLeadsPage — inbox of zero-result public searches.
//
// Every failed search on the ⌘K CommandBar or the /blog search bar posts
// to /api/public/producer-leads. This page reads them back so Audrey can:
//   • see which producers visitors are asking for (sorted by demand)
//   • one-click "Créer maintenant" → AI-generate + save fiche upstream
//   • mark leads as actioned or dismissed
//
// Signal shape: {key, first_query, last_query, count, last_seen_at,
// sources[], langs[], status}. Sort is (count desc, last_seen_at desc).
import { useEffect, useState } from "react";
import { Inbox, Loader2, Sparkles, Check, X as XIcon, RefreshCw, Trash2, ExternalLink, MapPin, Grape, Building2, HelpCircle } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const STATUS_TABS = [
  { key: "new", label: "Nouvelles", chip: "bg-[color:var(--gv-pink)] text-white" },
  { key: "actioned", label: "Créées", chip: "bg-emerald-100 text-emerald-700" },
  { key: "dismissed", label: "Rejetées", chip: "bg-neutral-200 text-neutral-600" },
  { key: "all", label: "Toutes", chip: "bg-white border border-[color:var(--gv-line)] text-[color:var(--gv-ink)]" },
];

// Kind_intent filter chips. Order = same as public UI (producer first).
const KIND_TABS = [
  { key: "all", label: "Tous types", Icon: Inbox },
  { key: "producer", label: "Producteurs", Icon: Building2 },
  { key: "appellation", label: "Appellations", Icon: MapPin },
  { key: "cepage", label: "Cépages", Icon: Grape },
  { key: "any", label: "Non classés", Icon: HelpCircle },
];

const KIND_META = {
  producer: { label: "Producteur", Icon: Building2 },
  appellation: { label: "Appellation", Icon: MapPin },
  cepage: { label: "Cépage", Icon: Grape },
  any: { label: "Non classé", Icon: HelpCircle },
};

const AdminProducerLeadsPage = () => {
  const [status, setStatus] = useState("new");
  const [kindFilter, setKindFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [countsByKind, setCountsByKind] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.listProducerLeads(status, kindFilter, 200);
      setItems(res?.items || []);
      setCountsByKind(res?.counts_by_kind || {});
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [status, kindFilter]);

  const generate = async (lead) => {
    setBusyKey(lead.key);
    setError(null);
    try {
      const draft = await adminApi.aiGenerateProducer({
        name: lead.last_query || lead.first_query,
        region: "",
        website: "",
        extra_instructions: `Sourced from a visitor request (searched ${lead.count} times).`,
      });
      const saved = await adminApi.upsertProducer(draft);
      if (saved?.slug) {
        await adminApi.setProducerLeadStatus(lead.key, "actioned");
        await load();
      }
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusyKey(null);
    }
  };

  const setLeadStatus = async (key, newStatus) => {
    setBusyKey(key);
    try {
      await adminApi.setProducerLeadStatus(key, newStatus);
      await load();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusyKey(null);
    }
  };

  const remove = async (key) => {
    if (!window.confirm("Supprimer définitivement cette suggestion ?")) return;
    setBusyKey(key);
    try {
      await adminApi.deleteProducerLead(key);
      await load();
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-producer-leads-page">
      <header className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)]">
            <Inbox size={12} /> File Fiches à créer
          </div>
          <h1 className="font-display text-3xl text-[color:var(--gv-ink)] mt-2">Suggestions de visiteurs</h1>
          <p className="text-[13px] text-[color:var(--gv-ink)]/60 mt-1 max-w-2xl">
            Chaque recherche « 0 résultat » sur <code>⌘K</code> ou <code>/blog</code> alimente cette file — producteurs, appellations et cépages confondus. Trie par nombre de demandes, priorise, et lance la génération d&apos;une fiche en un clic.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-[5px] bg-white border border-[color:var(--gv-line)] hover:border-[color:var(--gv-ink)]/30 text-[13px]"
          data-testid="leads-refresh"
        >
          <RefreshCw size={12} /> Rafraîchir
        </button>
      </header>

      {/* Kind filter chips — top row (what the visitor was after) */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="leads-kind-tabs">
        {KIND_TABS.map((tab) => {
          const Icon = tab.Icon;
          const c = countsByKind[tab.key] ?? 0;
          const active = kindFilter === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setKindFilter(tab.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] text-[12.5px] transition-colors ${
                active
                  ? "bg-[color:var(--gv-pink)] text-white"
                  : "bg-white text-[color:var(--gv-ink)] border border-[color:var(--gv-line)] hover:border-[color:var(--gv-ink)]/30"
              }`}
              data-testid={`leads-kind-${tab.key}`}
            >
              <Icon size={11} />
              {tab.label}
              <span className={`text-[11px] ${active ? "opacity-75" : "opacity-45"}`}>({c})</span>
            </button>
          );
        })}
      </div>

      {/* Status tabs — bottom row (workflow state) */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="leads-tabs">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatus(tab.key)}
            className={`px-3 py-1.5 rounded-[5px] text-[12.5px] transition-colors ${
              status === tab.key
                ? "bg-[color:var(--gv-ink)] text-white"
                : "bg-white text-[color:var(--gv-ink)] border border-[color:var(--gv-line)] hover:border-[color:var(--gv-ink)]/30"
            }`}
            data-testid={`leads-tab-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-[4px] p-3 text-[12.5px] text-red-700" data-testid="leads-error">
          {error}
        </div>
      )}

      {/* List */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[color:var(--gv-ink)]/50 text-[13px] flex items-center justify-center gap-2">
            <Loader2 size={13} className="animate-spin" /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="p-14 text-center" data-testid="leads-empty">
            <Inbox size={26} className="mx-auto text-[color:var(--gv-ink)]/25 mb-4" />
            <div className="font-display text-lg text-[color:var(--gv-ink)]">Aucune suggestion pour le moment</div>
            <p className="text-[12.5px] text-[color:var(--gv-ink)]/55 mt-2 max-w-md mx-auto">
              Dès qu&apos;un visiteur cherchera un producteur absent du catalogue, sa demande apparaîtra ici.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-[color:var(--gv-line)]" data-testid="leads-list">
            {items.map((lead) => {
              const isBusy = busyKey === lead.key;
              const kind = lead.last_kind_intent || "any";
              const kindMeta = KIND_META[kind] || KIND_META.any;
              const KindIcon = kindMeta.Icon;
              // For now only producer leads have a one-click generator
              // hooked up; appellation/cepage leads route Audrey to the
              // dedicated admin generators (Atlas · IA) with the query
              // pre-filled via URL param.
              const canOneClick = kind === "producer" || kind === "any";
              return (
                <li key={lead.key} className="p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`lead-row-${lead.key}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2.5 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-widest text-[color:var(--gv-pink)]">
                        <KindIcon size={10} /> {kindMeta.label}
                      </span>
                      <h3 className="font-display text-[17px] text-[color:var(--gv-ink)]">{lead.last_query || lead.first_query}</h3>
                      <span className={`text-[10.5px] uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_TABS.find((t) => t.key === lead.status)?.chip || "bg-neutral-100"}`}>
                        {lead.status}
                      </span>
                      <span className="text-[11px] text-[color:var(--gv-pink)] font-medium">
                        {lead.count} demande{lead.count > 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="text-[11.5px] text-[color:var(--gv-ink)]/55 mt-1.5 flex items-center gap-3 flex-wrap">
                      <span>
                        Dernière : {new Date(lead.last_seen_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                      {lead.sources?.length > 0 && (
                        <span>· via {lead.sources.join(", ")}</span>
                      )}
                      {lead.langs?.length > 0 && (
                        <span>· {lead.langs.join(" / ")}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {lead.status !== "actioned" && canOneClick && (
                      <button
                        onClick={() => generate(lead)}
                        disabled={isBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[12px] font-medium hover:bg-[color:var(--gv-ink)] transition-colors disabled:opacity-60"
                        data-testid={`lead-generate-${lead.key}`}
                        title="Générer la fiche producteur avec l'IA et l'enregistrer dans le KMS"
                      >
                        {isBusy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                        Créer producteur
                      </button>
                    )}
                    {lead.status !== "actioned" && !canOneClick && (
                      <a
                        href={`/admin/app-atlas?type=${kind}&q=${encodeURIComponent(lead.last_query || lead.first_query)}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] bg-white border border-[color:var(--gv-pink)] text-[color:var(--gv-pink)] text-[12px] font-medium hover:bg-[color:var(--gv-pink)] hover:text-white transition-colors"
                        data-testid={`lead-open-atlas-${lead.key}`}
                      >
                        <Sparkles size={11} /> Ouvrir Atlas · IA
                      </a>
                    )}
                    {lead.status === "actioned" && (
                      <a
                        href={`/producteur/${lead.key.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px] bg-emerald-50 text-emerald-700 text-[12px] font-medium hover:bg-emerald-100 transition-colors"
                      >
                        <Check size={11} /> Voir la fiche <ExternalLink size={10} />
                      </a>
                    )}
                    {lead.status !== "dismissed" && (
                      <button
                        onClick={() => setLeadStatus(lead.key, "dismissed")}
                        disabled={isBusy}
                        className="p-1.5 rounded-[4px] text-[color:var(--gv-ink)]/50 hover:text-[color:var(--gv-ink)] hover:bg-[color:var(--gv-mist)] disabled:opacity-60"
                        title="Rejeter"
                        data-testid={`lead-dismiss-${lead.key}`}
                      >
                        <XIcon size={13} />
                      </button>
                    )}
                    <button
                      onClick={() => remove(lead.key)}
                      disabled={isBusy}
                      className="p-1.5 rounded-[4px] text-[color:var(--gv-ink)]/40 hover:text-red-600 hover:bg-red-50 disabled:opacity-60"
                      title="Supprimer"
                      data-testid={`lead-delete-${lead.key}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminProducerLeadsPage;
